import React, { useEffect } from 'react';
import { Box, Container, Typography, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { CTAS, HERO_HEADLINES, HERO_SUBHEADLINE } from '../landingContent';
import LandingCtaButton from '../shared/LandingCtaButton';
import HeroFlowVisual from '../shared/HeroFlowVisual';
import { LANDING_COLORS, LANDING_FONTS, LANDING_LIGHT_BAND_BG } from '../landingVisual';
import { SITE_NAME } from '../../../config/seoConfig';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap';

function useLandingFonts() {
  useEffect(() => {
    const id = 'landing-dm-sans';
    if (document.getElementById(id)) return undefined;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
    return undefined;
  }, []);
}

const HeroSection = () => {
  useLandingFonts();

  return (
    <Box
      component="section"
      data-testid="landing-hero"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: LANDING_COLORS.ink,
        fontFamily: LANDING_FONTS.sans,
        background: LANDING_LIGHT_BAND_BG,
        py: { xs: 9, md: 12 },
        textAlign: 'center',
      }}
    >
      {/* Subtle grid atmosphere */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          inset: 0,
          opacity: 0.55,
          backgroundImage: `
            linear-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15, 23, 42, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 75%)',
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          component="p"
          sx={{
            fontFamily: LANDING_FONTS.sans,
            fontWeight: 800,
            letterSpacing: '-0.04em',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            mb: 3,
            color: LANDING_COLORS.ink,
          }}
        >
          {SITE_NAME}
        </Typography>

        <Typography
          variant="h1"
          sx={{
            fontFamily: LANDING_FONTS.sans,
            fontWeight: 700,
            letterSpacing: '-0.035em',
            fontSize: { xs: '1.85rem', sm: '2.35rem', md: '2.85rem' },
            lineHeight: 1.12,
            mb: 2.5,
            maxWidth: 820,
            mx: 'auto',
          }}
        >
          {HERO_HEADLINES.map((line) => (
            <Box key={line} component="span" sx={{ display: 'block' }}>
              {line}
            </Box>
          ))}
        </Typography>

        <Typography
          component="p"
          sx={{
            mb: 4,
            maxWidth: 640,
            mx: 'auto',
            fontWeight: 400,
            fontSize: { xs: '1.05rem', md: '1.15rem' },
            lineHeight: 1.65,
            color: LANDING_COLORS.onLightMuted,
          }}
        >
          {HERO_SUBHEADLINE}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            justifyContent: 'center',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <LandingCtaButton
            to={CTAS.practice.to}
            label={CTAS.practice.label}
            variant="candidate"
          />
          <LandingCtaButton
            to={CTAS.signIn.to}
            label={CTAS.signIn.label}
            variant="secondary"
          />
        </Box>

        <Typography
          variant="body2"
          sx={{ mt: 2.5, color: LANDING_COLORS.onLightMuted, fontSize: '0.9rem', lineHeight: 1.7 }}
        >
          Alumni or interviewer?{' '}
          <MuiLink
            component={RouterLink}
            to={CTAS.interviewer.to}
            underline="hover"
            data-testid="hero-become-interviewer"
            sx={{ color: LANDING_COLORS.accent, fontWeight: 600 }}
          >
            Help juniors with mocks
          </MuiLink>
          <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {' · '}
            </Box>
            Hiring team?{' '}
            <MuiLink
              component={RouterLink}
              to={CTAS.demo.to}
              underline="hover"
              sx={{ color: LANDING_COLORS.accent, fontWeight: 600 }}
            >
              {CTAS.demo.label}
            </MuiLink>
          </Box>
        </Typography>

        <HeroFlowVisual />
      </Container>
    </Box>
  );
};

export default HeroSection;
